/* eslint-disable @typescript-eslint/no-explicit-any */

import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { Page, ReviewAccordion } from '../../components';
import { ErrorIcon } from '../../components/ErrorValidation';
import { logger } from '../../features/logger';
import { withAPIService } from '../../networking';
import { CARD_CATEGORIES } from '../../shared/cardConfig/constants';
import { reviewNoError, reviewWithError } from '../../shared/dialogConfigs';
import { withStore } from '../../store';
import { openDialog } from '../../store/dialog';
import { handleSubmit } from '../../utils/formHelpers';
import { hasFieldError } from '../../utils/validation';
import AutoValidation from './AutoValidation';

function isFunction(fn: unknown): fn is Function {
  return !!fn && Object.prototype.toString.call(fn) === '[object Function]';
}

const ErrorSubtitle = styled.h4`
  color: var(--Text-text-primary, #101c16);
  margin-left: 8px;
  font: var(--sprout-font-body1);
`;

const Subtitle = styled.p`
  font-size: 20px;
  line-height: 26px;
  font-weight: 700;
  color: var(--Text-text-primary, #101c16);
  padding-right: 8px;

  @media only screen and (max-width: 700px) {
    font-size: 18px;
    line-height: 24px;
    padding-right: 5px;
  }
`;

const SubtitleWrapper = styled.div`
  display: flex;
  align-items: center;

  @media only screen and (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

interface Props {
  history: any;
  store: any;
  apiService: any;
  dispatch: any;
  accordions?: any[];
}

function Component(props: Props): JSX.Element {
  const { store, accordions = [], history, apiService, dispatch } = props;

  const { customer } = store;
  const { common: { policyAccepted = false } = {} } = store;

  const { search } = history.location;
  const { t } = useTranslation();

  useEffect(() => {
    logger.info(`Red-Carpet-FE Review Page`, {
      hcRequestId: customer?.id
    });
  }, [store]);

  const [validated, setValidation] = useState<boolean>(false);

  const [signingEvent, setSigningEvent] = useState<boolean>(!!search && search.includes('event=signing_complete'));

  const disableSubmit =
    hasFieldError({
      fieldError: store.fieldError,
      keys: Object.values(CARD_CATEGORIES)
    }) ||
    !validated ||
    !policyAccepted;

  useEffect(() => {
    if (signingEvent) {
      apiService
        .validateCustomer(customer, dispatch)
        .then(() => {
          dispatch(
            openDialog(
              reviewNoError(() =>
                handleSubmit({
                  disableSubmitFunc: disableSubmit,
                  apiService,
                  customer,
                  dispatch,
                  history
                })
              )
            )
          );
          setValidation(true);
          setSigningEvent(false);
        })
        .catch(() => {
          dispatch(openDialog(reviewWithError()));
          setSigningEvent(false);
        });
    }
  }, [signingEvent, customer, apiService, dispatch, history, disableSubmit]);

  const checkPolicyError = (cards: any[]) => cards.find(card => card.key === 'privacyPolicy' && !policyAccepted);

  const checkRootLevelError = (cards: any[]) => {
    const keys = Object.keys(store.fieldError || {});

    return cards.find(card => {
      if (card.fieldWrapper && keys.includes(card.fieldWrapper)) {
        const inner = Object.keys(store.fieldError[card.fieldWrapper]);
        return inner.includes(card.key);
      }

      if (keys.includes(card.key)) {
        const errors = Object.keys(store.fieldError[card.key]);
        return card.errorMustContain ? errors.includes(card.errorMustContain) : true;
      }

      return false;
    });
  };

  const accordionHasError = (cards: any[], index: number) => {
    const keys = Object.keys(store.fieldError || {});
    if (checkRootLevelError(cards) || checkPolicyError(cards)) {
      return true;
    }

    return keys.some(key => {
      const val = store.fieldError[key];
      return Array.isArray(val) && val[index] && cards.find(card => Object.keys(val[index]).includes(card.key));
    });
  };
  const generateAccordionProps = (accordion: any, index: number): any => {
    if (Array.isArray(accordion)) {
      return accordion.map((a, i) => generateAccordionProps(a, i));
    }

    const accordionError = accordionHasError(accordion.cards, index);

    const AccordionSubtitle = () =>
      accordionError ? (
        <SubtitleWrapper>
          {accordion.subtitle && <Subtitle>{accordion.subtitle}</Subtitle>}
          <ErrorRow>
            <ErrorIcon />
            <ErrorSubtitle>{t('errors.reviewPageTabError')}</ErrorSubtitle>
          </ErrorRow>
        </SubtitleWrapper>
      ) : accordion.subtitle ? (
        <SubtitleWrapper>
          <Subtitle>{accordion.subtitle}</Subtitle>
        </SubtitleWrapper>
      ) : null;

    return {
      title: accordion.title,
      count: accordion.count,
      shouldOpenOnInitialLoad: accordionError,
      body: accordion.cards
        .map((cardEntry: any) => {
          if (cardEntry?.hidden) return null;

          if (typeof cardEntry?.shouldRender === 'function' && !cardEntry.shouldRender({ ...props, store }, index))
            return null;

          if (!cardEntry.Component) return null;

          const { key, ...card } = cardEntry;

          const render = () => <card.Component index={index} {...props} {...card} store={store} isReview />;

          (render as any).__reviewCardKey = key;

          return render;
        })
        .filter(Boolean),
      shouldAlwaysShowSubtitle: true,
      Subtitle: AccordionSubtitle
    };
  };
  return (
    <Page {...props} disableProgress={disableSubmit} isReview>
      {signingEvent ? null : (
        <>
          <ReviewAccordion
            canHaveMultipleOpen
            canRunInitialOpenTabs={validated}
            editMode={store.editMode}
            initializer={() => Array.from(Array(accordions.length + 2)).map((_, i) => i)}
            data={accordions
              .map(acc => (isFunction(acc) ? generateAccordionProps(acc(props), 0) : generateAccordionProps(acc, 0)))
              .flat()}
          />

          <AutoValidation {...props} setValidation={setValidation} validated={validated} />
        </>
      )}
    </Page>
  );
}

/* ----------------------------------
   HOCs
---------------------------------- */

const connectAPI = withAPIService(Component);
const ReviewPage = withStore(connectAPI);

export default ReviewPage;
